import React, { useState } from 'react';
import { 
  Mail, Shield, AlertTriangle, CheckCircle2, XCircle, ArrowRight, 
  AtSign, FileWarning, Link2, Info, Lightbulb, User, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress, ScoreRing } from '@/components/ui/progress';
import { runEmailAnalysis } from '@/lib/mlClient';
import { MLEmailInsights, PlainSummary, MLReasoning } from './MLInsights';
import { explainEmail } from '@/lib/plainExplanation';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

// Responsive Score Ring
const EmailScoreRing = ({ score, size = 140, mobileSize = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const mobileRadius = (mobileSize - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const mobileCircumference = mobileRadius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const mobileOffset = mobileCircumference - (score / 100) * mobileCircumference;
  
  const getColor = () => {
    if (score >= 80) return "hsl(var(--success))";
    if (score >= 50) return "hsl(var(--warning))";
    return "hsl(var(--destructive))";
  };

  const getLabel = () => {
    if (score >= 80) return "Safe";
    if (score >= 50) return "Caution";
    return "Risk";
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={mobileSize} height={mobileSize} className="transform -rotate-90 sm:hidden">
        <circle cx={mobileSize / 2} cy={mobileSize / 2} r={mobileRadius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} />
        <circle cx={mobileSize / 2} cy={mobileSize / 2} r={mobileRadius} fill="none" stroke={getColor()} strokeWidth={strokeWidth} strokeDasharray={mobileCircumference} strokeDashoffset={mobileOffset} strokeLinecap="round" className="transition-all duration-700 ease-out" />
      </svg>
      <svg width={size} height={size} className="transform -rotate-90 hidden sm:block">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={getColor()} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-2xl sm:text-3xl font-bold font-display" style={{ color: getColor() }}>{score}</span>
        <span className="text-xs text-muted-foreground font-medium">{getLabel()}</span>
      </div>
    </div>
  );
};

// Score Display Component
const EmailScoreDisplay = ({ result }) => {
  const getVerdictConfig = () => {
    switch (result.verdict) {
      case 'Likely Legitimate':
        return { icon: CheckCircle2, color: 'text-success', border: 'border-success/30', badge: 'safe' };
      case 'Suspicious':
        return { icon: AlertTriangle, color: 'text-warning', border: 'border-warning/30', badge: 'suspicious' };
      case 'High Phishing Risk':
        return { icon: XCircle, color: 'text-destructive', border: 'border-destructive/30', badge: 'danger' };
      default:
        return { icon: Info, color: 'text-muted-foreground', border: 'border-border', badge: 'unknown' };
    }
  };

  const config = getVerdictConfig();
  const Icon = config.icon;
  const safetyScore = 100 - result.overallRiskScore;

  return (
    <GlassCard className={cn("p-4 sm:p-6", config.border, "border-2")}>
      <div className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-4 lg:gap-6">
        <div className="flex-shrink-0">
          <EmailScoreRing score={safetyScore} />
        </div>
        
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
            <Badge variant={config.badge} className="text-sm">
              <Icon className="w-4 h-4 mr-1" />
              {result.verdict}
            </Badge>
            {result.hardRedFlags && result.hardRedFlags.length > 0 && (
              <Badge variant="danger" className="text-xs">
                {result.hardRedFlags.length} Critical
              </Badge>
            )}
          </div>
          
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-foreground">
              Email Safety Score
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {result.verdict === 'Likely Legitimate' 
                ? "This email appears to be from a legitimate source."
                : result.verdict === 'Suspicious'
                ? "Some concerns detected. Verify sender through official channels."
                : "Multiple phishing indicators detected. Exercise extreme caution."}
            </p>
          </div>

          {/* Safety Bar */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-muted-foreground">Safety Level</span>
              <span className="text-xs font-mono">
                <span className={cn("font-bold", config.color)}>{safetyScore}</span>
                <span className="text-muted-foreground">/100</span>
              </span>
            </div>
            <div className="relative h-3 sm:h-4 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full transition-all duration-500 ease-out rounded-full",
                  safetyScore >= 80 ? 'bg-success' :
                  safetyScore >= 50 ? 'bg-warning' : 'bg-destructive'
                )}
                style={{ width: `${safetyScore}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

// Risk Details Component
const EmailRiskDetails = ({ result }) => {
  return (
    <div className="space-y-4">
      {/* Hard Rule Alert */}
      {result.hardRuleTriggered && (
        <div className="p-4 rounded-xl bg-destructive/10 border-2 border-destructive/30">
          <div className="flex items-start gap-2">
            <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-sm text-destructive">CREDENTIAL/OTP REQUEST DETECTED</span>
              <p className="text-xs sm:text-sm text-destructive/90 mt-1">{result.hardRuleTriggered}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Sender Analysis */}
        <GlassCard className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            </div>
            <h4 className="text-sm font-semibold">Sender Analysis</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Risk Score</span>
              <Badge variant={
                result.senderAnalysis.riskScore >= 60 ? 'danger' :
                result.senderAnalysis.riskScore >= 30 ? 'suspicious' : 'safe'
              } className="text-xs">
                {result.senderAnalysis.riskScore}/100
              </Badge>
            </div>
            <Progress 
              value={result.senderAnalysis.riskScore} 
              variant={result.senderAnalysis.riskScore >= 60 ? 'danger' : result.senderAnalysis.riskScore >= 30 ? 'warning' : 'success'}
            />
            <div className="space-y-1.5 mt-2">
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Domain</span>
                <span className="text-[10px] sm:text-xs font-mono truncate max-w-[120px]">{result.senderAnalysis.details.domain}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50">
                <span className="text-[10px] sm:text-xs text-muted-foreground">TLD</span>
                <span className="text-[10px] sm:text-xs font-mono">{result.senderAnalysis.details.tld || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Free Provider</span>
                <Badge variant={result.senderAnalysis.details.isFreeProvider ? 'suspicious' : 'safe'} className="text-[10px]">
                  {result.senderAnalysis.details.isFreeProvider ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Content Analysis */}
        <GlassCard className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
              <FileWarning className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-secondary" />
            </div>
            <h4 className="text-sm font-semibold">Content Analysis</h4>
          </div>
          {result.contentAnalysis ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Risk Score</span>
                <Badge variant={
                  result.contentAnalysis.riskScore >= 60 ? 'danger' :
                  result.contentAnalysis.riskScore >= 30 ? 'suspicious' : 'safe'
                } className="text-xs">
                  {result.contentAnalysis.riskScore}/100
                </Badge>
              </div>
              <Progress 
                value={result.contentAnalysis.riskScore} 
                variant={result.contentAnalysis.riskScore >= 60 ? 'danger' : result.contentAnalysis.riskScore >= 30 ? 'warning' : 'success'}
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="text-center p-2 rounded bg-muted/50">
                  <div className={cn("text-base sm:text-lg font-bold", result.contentAnalysis.summary.urgencyPhraseCount > 0 ? "text-warning" : "text-success")}>
                    {result.contentAnalysis.summary.urgencyPhraseCount}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Urgency</div>
                </div>
                <div className="text-center p-2 rounded bg-muted/50">
                  <div className={cn("text-base sm:text-lg font-bold", (result.contentAnalysis.summary.credentialRequestCount + result.contentAnalysis.summary.otpRequestCount) > 0 ? "text-destructive" : "text-success")}>
                    {result.contentAnalysis.summary.credentialRequestCount + result.contentAnalysis.summary.otpRequestCount}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Cred Req</div>
                </div>
                <div className="text-center p-2 rounded bg-muted/50">
                  <div className={cn("text-base sm:text-lg font-bold", result.contentAnalysis.summary.dangerousLinkCount > 0 ? "text-destructive" : "text-success")}>
                    {result.contentAnalysis.summary.dangerousLinkCount}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Bad Links</div>
                </div>
                <div className="text-center p-2 rounded bg-muted/50">
                  <div className="text-base sm:text-lg font-bold text-primary">
                    {result.contentAnalysis.summary.extractedLinks.length}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Links</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <FileWarning className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-xs">No content provided</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Top Risk Factors */}
      {result.topReasons && result.topReasons.length > 0 && (
        <GlassCard className="p-4 sm:p-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            Top Risk Factors
          </h4>
          <div className="space-y-2">
            {result.topReasons.map((flag, index) => (
              <div 
                key={index}
                className={cn(
                  "p-3 rounded-lg border",
                  flag.isHardRedFlag ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'high' ? 'bg-warning/10 border-warning/30' :
                  'bg-muted/50 border-border'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-background flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="font-medium text-xs sm:text-sm truncate">{flag.title}</span>
                    {flag.isHardRedFlag && (
                      <Badge variant="danger" className="text-[10px] flex-shrink-0">CRITICAL</Badge>
                    )}
                  </div>
                  <span className="text-xs font-mono text-destructive flex-shrink-0">+{flag.penalty}</span>
                </div>
                <p className="text-xs text-muted-foreground ml-7">{flag.description}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Recommendations */}
      <GlassCard className="p-4 sm:p-5">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-primary" />
          Recommendations
        </h4>
        <ul className="space-y-2">
          {result.recommendations.map((rec, index) => (
            <li key={index} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
              <ArrowRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>{rec}</span>
            </li>
          ))}
        </ul>
      </GlassCard>
    </div>
  );
};

export const EmailVerification = () => {
  const [emailAddress, setEmailAddress] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { addVerification } = useApp();

  const handleVerify = async () => {
    if (!emailAddress.trim()) return;
    
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const verificationResult = await runEmailAnalysis(emailAddress, emailContent);
    setResult(verificationResult);
    
    addVerification({
      type: 'email',
      input: emailAddress,
      verdict: verificationResult.verdict,
      riskScore: verificationResult.overallRiskScore,
      flagCount: verificationResult.flags.length,
      riskLevel: verificationResult.verdict === 'Likely Legitimate' ? 'Low' : 
                 verificationResult.verdict === 'Suspicious' ? 'Medium' : 'High',
    });
    
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !showContent) handleVerify();
  };

  const exampleEmails = [
    { email: 'support@google.com', label: 'Legitimate' },
    { email: 'security@g00gle-verify.xyz', label: 'Phishing' },
    { email: 'admin@paypa1-secure.tk', label: 'Typosquatting' },
  ];

  const exampleContent = `Dear Valued Customer,

URGENT: Your account has been suspended due to suspicious activity!

You must verify your identity immediately or your account will be closed within 24 hours.

Click here: http://g00gle-login.xyz/verify?id=12345

Please provide:
- Your password
- Social Security Number
- Credit card details

Best regards,
Security Team`;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">Email Checker</h2>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Analyze emails for phishing indicators and security risks.
        </p>
      </div>

      {/* Desktop Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Input */}
        <div className="space-y-6">
          <GlassCard className="p-4 sm:p-6">
            <div className="space-y-4">
              {/* Email Address */}
              <div>
                <label className="text-xs sm:text-sm font-medium text-foreground mb-2 block">
                  Sender Email <span className="text-destructive">*</span>
                </label>
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      type="email"
                      placeholder="sender@example.com"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="h-12 sm:h-14 pl-10 sm:pl-12 text-base w-full"
                      data-testid="email-input"
                    />
                    <AtSign className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  </div>
                  
                  {!showContent && (
                    <Button 
                      onClick={handleVerify} 
                      disabled={!emailAddress.trim() || isLoading}
                      className="w-full h-12 text-base"
                      variant="gradient"
                      data-testid="check-button"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      ) : (
                        <>
                          <Shield className="w-5 h-5 mr-2" />
                          Check Email
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Toggle Content */}
              <Button
                variant={showContent ? "default" : "outline"}
                size="sm"
                onClick={() => setShowContent(!showContent)}
                className="text-xs h-9"
              >
                <FileWarning className="w-4 h-4 mr-1" />
                {showContent ? 'Hide Content' : 'Add Email Body (Optional)'}
              </Button>

              {/* Email Content */}
              {showContent && (
                <div className="animate-fade-in space-y-3">
                  <Textarea
                    placeholder="Paste email content here..."
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    className="min-h-[120px] sm:min-h-[150px] text-base resize-none w-full"
                  />
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {emailContent.length > 0 && `${emailContent.split(/\s+/).filter(w => w).length} words`}
                    </span>
                    <Button 
                      onClick={handleVerify} 
                      disabled={!emailAddress.trim() || isLoading}
                      className="w-full sm:w-auto h-12 text-base"
                      variant="gradient"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      ) : (
                        <>
                          <Shield className="w-5 h-5 mr-2" />
                          Analyze Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Examples */}
              <div className="pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2">Try examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleEmails.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setEmailAddress(example.email)}
                      className={cn(
                        "px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[36px]",
                        example.label === 'Legitimate'
                          ? "bg-success/10 hover:bg-success/20 text-success"
                          : "bg-destructive/10 hover:bg-destructive/20 text-destructive"
                      )}
                    >
                      {example.label}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setEmailAddress('security@g00gle-verify.xyz');
                      setEmailContent(exampleContent);
                      setShowContent(true);
                    }}
                    className="px-3 py-2 rounded-lg text-xs font-medium bg-destructive/10 hover:bg-destructive/20 text-destructive transition-colors min-h-[36px]"
                  >
                    Full phishing
                  </button>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Desktop: Show Score in left column */}
          {result && (
            <div className="hidden lg:block">
              <EmailScoreDisplay result={result} />
            </div>
          )}

          {/* Info Cards */}
          {!result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-destructive/10 flex items-center justify-center mb-2">
                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Domain Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Detects typosquatting and impersonation.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center mb-2">
                    <FileWarning className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Content Scanning</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Identifies urgency and credential requests.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center mb-2">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Phishing Detection</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Hard red flags for credential requests.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Right Column: Results */}
        {result && (
          <div className="space-y-6">
            <div className="lg:hidden">
              <EmailScoreDisplay result={result} />
            </div>
            <PlainSummary summary={explainEmail(result)} testId="email-plain-summary" />
            <EmailRiskDetails result={result} />
            <MLReasoning testId="email-ml-toggle">
              <MLEmailInsights ml={result.ml} />
            </MLReasoning>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
